// app/src/api/encounterHistoryApi.ts

import { createAsyncThunk } from "@reduxjs/toolkit"
import { serverApi, handleApiResponse, queApi } from "@core/api/core"
import { EncounterWIthItemInvoiceSerializer } from "@/types/encounterItem"
import { EncounterDatesSerializer, EncounterExternalNoteDatesSerializer } from "@/types/encounter"
import { PatientEncounterAnalysisResult } from "../../types/patientAnalysis"
import type { RootState } from "@core/store"
import { getStoredWebSessionUser } from "@core/utils/webSessionAuth"

export interface EncounterHistoryPagination {
  hasMore: boolean
  nextBeforeId: number | null
  limit?: number
}

export interface EncounterDisRegCodeHistoryItem {
  disRegCode: string
  disRegType: number
  disRegTypeLabel: string
  disRegKcd: string
  encounterDate: string
}

export interface FirstInsuranceEncounterResult {
  firstEncounterDate: string
  encounterUuid: string
}

export interface FetchEncounterHistoryParams {
  uuid: string
  limit?: number
  beforeId?: number
  pinnedFirst?: boolean
  append?: boolean
  reuseRecent?: boolean
  force?: boolean
  silent?: boolean
}

type EncounterHistoryPageResult = {
  results: EncounterWIthItemInvoiceSerializer[]
  pagination?: EncounterHistoryPagination
}

type EncounterHistoryPageCacheEntry = {
  result?: EncounterHistoryPageResult
  expiresAt?: number
  request?: Promise<EncounterHistoryPageResult>
}

const ENCOUNTER_HISTORY_INITIAL_PAGE_REUSE_MS = 30_000
const ENCOUNTER_HISTORY_DEFAULT_PAGE_LIMIT = 10
const encounterHistoryInitialPageCache = new Map<string, EncounterHistoryPageCacheEntry>()
const LOCAL_ENCOUNTER_HISTORY_EVENT_TTL_MS = 30_000
const localEncounterHistoryEvents = new Map<string, { patientUuid: string; expiresAt: number }>()

const pruneExpiredLocalEncounterHistoryEvents = (now: number) => {
  localEncounterHistoryEvents.forEach((entry, key) => {
    if (entry.expiresAt <= now) {
      localEncounterHistoryEvents.delete(key)
    }
  })
}

export const clearEncounterHistoryInitialPageCache = () => {
  encounterHistoryInitialPageCache.clear()
  localEncounterHistoryEvents.clear()
}

export const invalidateEncounterHistoryInitialPageCache = (patientUuid: string) => {
  const patientCacheKeyPart = `:${patientUuid}:`
  encounterHistoryInitialPageCache.forEach((_entry, key) => {
    if (key.includes(patientCacheKeyPart)) {
      encounterHistoryInitialPageCache.delete(key)
    }
  })
}

export const registerLocalEncounterHistoryEvent = (eventId: string, patientUuid: string) => {
  pruneExpiredLocalEncounterHistoryEvents(Date.now())
  localEncounterHistoryEvents.set(eventId, {
    patientUuid,
    expiresAt: Date.now() + LOCAL_ENCOUNTER_HISTORY_EVENT_TTL_MS,
  })
}

export const unregisterLocalEncounterHistoryEvent = (eventId: string) => {
  localEncounterHistoryEvents.delete(eventId)
}

export const isLocalEncounterHistoryEvent = (eventId: string | null | undefined, patientUuid: string): boolean => {
  if (!eventId) {
    return false
  }

  pruneExpiredLocalEncounterHistoryEvents(Date.now())

  const event = localEncounterHistoryEvents.get(eventId)
  return Boolean(event && event.patientUuid === patientUuid)
}

export const getEncounterHistoryPage = async (params: {
  uuid: string
  limit?: number
  beforeId?: number
  pinnedFirst?: boolean
  reuseRecent?: boolean
}): Promise<EncounterHistoryPageResult> => {
  const requestUrl = `/encounter/encounter_history/${params.uuid}/`
  const queryParams: Record<string, number | string> = {}
  const canReuseInitialPage = Boolean(params.reuseRecent && !params.beforeId)
  const authCacheKey = getStoredWebSessionUser()?.memberUuid || "anonymous"
  const cacheKey = `${authCacheKey}:${params.uuid}:${params.limit ?? ENCOUNTER_HISTORY_DEFAULT_PAGE_LIMIT}:${params.pinnedFirst ? "pinned" : "default"}`
  const now = Date.now()

  encounterHistoryInitialPageCache.forEach((entry, key) => {
    if (!entry.request && (entry.expiresAt || 0) <= now) {
      encounterHistoryInitialPageCache.delete(key)
    }
  })

  const cached = encounterHistoryInitialPageCache.get(cacheKey)

  if (canReuseInitialPage && cached?.result && (cached.expiresAt || 0) > now) {
    return cached.result
  }

  if (canReuseInitialPage && cached?.request) {
    return cached.request
  }

  if (params.limit) {
    queryParams.limit = params.limit
  }

  if (params.beforeId) {
    queryParams.before_id = params.beforeId
  }
  if (params.pinnedFirst) {
    queryParams.pinned_first = "true"
  }

  const request = serverApi.get(requestUrl, { params: queryParams }).then(response => {
    const payload = handleApiResponse<any>(response)

    return {
      results: (payload?.results ?? payload ?? []) as EncounterWIthItemInvoiceSerializer[],
      pagination: payload?.pagination,
    }
  })

  if (!params.beforeId) {
    encounterHistoryInitialPageCache.set(cacheKey, { request })
  }

  try {
    const result = await request

    if (!params.beforeId && encounterHistoryInitialPageCache.get(cacheKey)?.request === request) {
      encounterHistoryInitialPageCache.set(cacheKey, {
        result,
        expiresAt: Date.now() + ENCOUNTER_HISTORY_INITIAL_PAGE_REUSE_MS,
      })
    }

    return result
  } catch (error) {
    if (!params.beforeId && encounterHistoryInitialPageCache.get(cacheKey)?.request === request) {
      encounterHistoryInitialPageCache.delete(cacheKey)
    }
    throw error
  }
}

export const fetchEncounterHistory = createAsyncThunk<
  {
    data: EncounterWIthItemInvoiceSerializer[]
    pagination?: EncounterHistoryPagination
    append: boolean
  },
  FetchEncounterHistoryParams,
  { rejectValue: string }
>(
  "encounterHistory/fetchEncounterHistory",
  async ({ uuid, limit, beforeId, pinnedFirst, append, reuseRecent }, { rejectWithValue }) => {
    try {
      const { results, pagination } = await getEncounterHistoryPage({ uuid, limit, beforeId, pinnedFirst, reuseRecent })

      return {
        data: results,
        pagination,
        append: Boolean(append || beforeId),
      }
    } catch (error: any) {
      return rejectWithValue(error.message || "진료 히스토리 조회 중 오류가 발생했습니다.")
    }
  },
  {
    condition: ({ uuid, append, beforeId, force }, { getState }) => {
      const isAppend = Boolean(append || beforeId)
      const state = getState() as RootState
      if (isAppend) {
        const isSamePatientFirstPageLoading = state.encounterHistory.loading && state.encounterHistory.currentPatientUuid === uuid
        const isSamePatientAppendLoading = state.encounterHistory.loadingMore && state.encounterHistory.currentPatientUuid === uuid
        return !isSamePatientFirstPageLoading && !isSamePatientAppendLoading
      }
      if (force) return true

      const isSamePatientLoading = state.encounterHistory.loading && state.encounterHistory.currentPatientUuid === uuid

      return !isSamePatientLoading
    },
  }
)

export const getEncounterDates = async (params: { patientUuid: string; encounterMonth?: string }): Promise<EncounterDatesSerializer> => {
  const requestUrl = `/encounter/encounter_dates/`
  const response = await queApi.get(requestUrl, { params })
  return handleApiResponse<EncounterDatesSerializer>(response)
}

export const getEncounterExternalNoteDates = async (params: {
  patientUuid: string
  encounterMonth?: string
}): Promise<EncounterExternalNoteDatesSerializer> => {
  const requestUrl = `/encounter/external_note_dates/`
  const response = await queApi.get(requestUrl, { params })
  return handleApiResponse<EncounterExternalNoteDatesSerializer>(response)
}

export const getPatientEncounterAnalysis = async (patientUuid: string): Promise<PatientEncounterAnalysisResult> => {
  const requestUrl = `/encounter/encounter_history/${patientUuid}/analysis/`
  const response = await serverApi.get(requestUrl)
  return handleApiResponse<PatientEncounterAnalysisResult>(response)
}

/**
 * 이전 차트 반복용 - 지정 날짜 이전의 가장 최근 진료 1건 조회 (서버 직접 호출)
 */
export const getPreviousEncounter = async (
  patientUuid: string,
  beforeDate: string,
  encounterUuid: string
): Promise<EncounterWIthItemInvoiceSerializer | null> => {
  const requestUrl = `/encounter/encounter_history/${patientUuid}/previous/`
  const response = await serverApi.get(requestUrl, { params: { before_date: beforeDate, encounter_uuid: encounterUuid } })
  if (response?.data && typeof response.data === "object" && "result" in response.data) {
    return (response.data.result ?? null) as EncounterWIthItemInvoiceSerializer | null
  }
  return null
}

export const getEncounterDisRegCodeHistory = async (params: {
  patientUuid: string
  beforeDate?: string
}): Promise<EncounterDisRegCodeHistoryItem[]> => {
  const requestUrl = `/encounter/encounter_history/${params.patientUuid}/dis-reg-codes/`
  const response = await serverApi.get(requestUrl, {
    params: params.beforeDate ? { before_date: params.beforeDate } : undefined,
  })
  return handleApiResponse<EncounterDisRegCodeHistoryItem[]>(response)
}

export const getFirstInsuranceEncounter = async (params: {
  patientUuid: string
  insuranceUuid: string
}): Promise<FirstInsuranceEncounterResult | null> => {
  const requestUrl = `/encounter/encounter_history/${params.patientUuid}/first-insurance-encounter/`
  const response = await serverApi.get(requestUrl, {
    params: { insurance_uuid: params.insuranceUuid },
  })
  return handleApiResponse<FirstInsuranceEncounterResult | null>(response)
}
